import { DjiDetailStatus, DjiDraftPreview, DjiFlight } from '../../core/models/dji.model';
import { formatClockTime } from './date.utils';

const ML_PER_GALLON = 3785.411784;
const GRAMS_PER_POUND = 453.59237;
const MPS_TO_MPH = 2.2369362920544;

const NONE = '—';

/** DJI-built drones, including ADN's Talos units, which are DJI Agras. */
export function isDjiManufacturer(name: string | null | undefined): boolean {
  return !!name && /\b(dji|talos)\b/i.test(name);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "Jul 25, 8:00 AM" for a flight's start. Uses the wall-clock time where the work was when the Hub gave one, so
 * it reads like the logged times beside it. Without it (no known time zone) the instant is shown in the
 * viewer's own zone and labelled, so it is never mistaken for the typed times.
 */
export function formatDjiFlightStart(flight: Pick<DjiFlight, 'flightStartedAt' | 'flightStartedAtLocal'>): string {
  const local = flight.flightStartedAtLocal;
  const match = local ? /^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2})/.exec(local) : null;
  if (match) {
    return `${MONTHS[Number(match[2]) - 1]} ${Number(match[3])}, ${formatClockTime(match[4])}`;
  }
  if (!flight.flightStartedAt) return NONE;

  const instant = new Date(flight.flightStartedAt);
  if (isNaN(instant.getTime())) return NONE;
  return instant.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
}

export interface DjiDraftSummary {
  date: string;
  time: string;
  acres: string;
  flights: string;
  speed: string;
}

/** Display text for a draft preview: "Jul 28, 2026", "8:00 AM – 8:18 AM", "9.40 ac", "1", "13.0 mph". */
export function describeDjiDraft(draft: DjiDraftPreview): DjiDraftSummary {
  const date = /^(\d{4})-(\d{2})-(\d{2})/.exec(draft.applicationDate);
  const when = date ? `${MONTHS[Number(date[2]) - 1]} ${Number(date[3])}, ${date[1]}` : NONE;

  const start = formatClockTime(draft.startTime, '');
  const end = formatClockTime(draft.endTime, '');

  let speed: string;
  if (draft.flightSpeedMph != null) {
    speed = `${draft.flightSpeedMph.toFixed(1)} mph`;
  } else if (draft.detailStatus === 'processing') {
    speed = 'Not yet (DJI is still processing the flight details)';
  } else {
    speed = 'Not available';
  }

  return {
    date: when,
    time: start && end ? `${start} – ${end}` : NONE,
    acres: formatDjiAcres(draft.acreageSprayed),
    flights: String(draft.numFlights),
    speed,
  };
}

/** Plain label for a spray record's status; an unknown status is shown as it is. */
export function sprayRecordStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'pending':
      return 'Pending';
    case 'completed':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return status ?? NONE;
  }
}

/** "12 min", "1 h 5 min", "<1 min"; an em dash when unknown. */
export function formatDjiDuration(seconds: number | null | undefined): string {
  if (seconds == null || !isFinite(seconds) || seconds < 0) return NONE;
  if (seconds < 60) return '<1 min';

  const totalMinutes = Math.round(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes} min`;
  return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`;
}

export function formatDjiAcres(acres: number | null | undefined): string {
  if (acres == null || !isFinite(acres)) return NONE;
  return `${acres.toFixed(2)} ac`;
}

/** Applied product: spray in gallons, sow in pounds (DJI reports ml and g). */
export function formatDjiApplied(flight: Pick<DjiFlight, 'usageType' | 'sprayVolumeMl' | 'sowVolumeG'>): string {
  if (flight.usageType === 'sow') {
    return flight.sowVolumeG == null ? NONE : `${(flight.sowVolumeG / GRAMS_PER_POUND).toFixed(1)} lb`;
  }
  if (flight.sprayVolumeMl == null) return NONE;
  return `${(flight.sprayVolumeMl / ML_PER_GALLON).toFixed(1)} gal`;
}

/** Total applied product across flights: gallons of spray, or pounds of sow when there is no spray. */
export function formatDjiAppliedTotal(totals: Pick<DjiFlightTotals, 'totalSprayMl' | 'totalSowG'>): string {
  if (totals.totalSprayMl != null) return `${(totals.totalSprayMl / ML_PER_GALLON).toFixed(1)} gal`;
  if (totals.totalSowG != null) return `${(totals.totalSowG / GRAMS_PER_POUND).toFixed(1)} lb`;
  return NONE;
}

export function formatDjiSpeed(mps: number | null | undefined): string {
  if (mps == null || !isFinite(mps)) return NONE;
  return `${(mps * MPS_TO_MPH).toFixed(1)} mph`;
}

export function djiDetailStatusLabel(status: DjiDetailStatus): string {
  switch (status) {
    case 'ready':
      return 'Flight details received';
    case 'processing':
      return 'Flight details processing';
    case 'unavailable':
      return 'Flight details not received';
  }
}

export interface DjiFlightTotals {
  flightCount: number;
  totalAcres: number | null;
  totalSprayMl: number | null;
  totalSowG: number | null;
  totalSeconds: number | null;
}

function sumKnown(values: Array<number | null>): number | null {
  const known = values.filter((v): v is number => v != null);
  return known.length === 0 ? null : known.reduce((a, b) => a + b, 0);
}

/** Totals across flights; a total is null only when no flight reported that figure. */
export function summarizeDjiFlights(flights: DjiFlight[]): DjiFlightTotals {
  return {
    flightCount: flights.length,
    totalAcres: sumKnown(flights.map((f) => f.workAreaAcres)),
    totalSprayMl: sumKnown(flights.map((f) => f.sprayVolumeMl)),
    totalSowG: sumKnown(flights.map((f) => f.sowVolumeG)),
    totalSeconds: sumKnown(flights.map((f) => f.durationSeconds)),
  };
}

/** "+3%", "-12%", "0%": the sign follows the ROUNDED figure, so a tiny difference reads "0%", never "-0%" or "+0%". */
export function formatDjiDifference(percent: number): string {
  if (!isFinite(percent)) return NONE;
  const rounded = Math.round(percent);
  if (rounded === 0) return '0%';
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
}

export interface AcreageComparison {
  planned: number;
  actual: number;
  /** Actual minus planned, as a percentage of planned (negative = under). */
  differencePercent: number;
}

/** DJI's covered area against the field's acreage; null when either is unknown or the field has none. */
export function compareAcreage(planned: number | null | undefined, actual: number | null | undefined): AcreageComparison | null {
  if (planned == null || actual == null || !isFinite(planned) || !isFinite(actual) || planned <= 0) return null;
  return { planned, actual, differencePercent: ((actual - planned) / planned) * 100 };
}

/**
 * Average speed across flights in m/s, weighted by flight time. Only flights
 * that reported a speed count; if none of those has a duration, a plain mean.
 * Null when no flight has reported a speed (the detail file may still be coming).
 */
export function averageSpeedMps(flights: DjiFlight[]): number | null {
  const withSpeed = flights.filter((f): f is DjiFlight & { avgSpeedMps: number } => f.avgSpeedMps != null);
  if (withSpeed.length === 0) return null;

  const timed = withSpeed.filter((f) => f.durationSeconds != null && f.durationSeconds > 0);
  if (timed.length === 0) {
    return withSpeed.reduce((sum, f) => sum + f.avgSpeedMps, 0) / withSpeed.length;
  }
  const seconds = timed.reduce((sum, f) => sum + (f.durationSeconds as number), 0);
  return timed.reduce((sum, f) => sum + f.avgSpeedMps * (f.durationSeconds as number), 0) / seconds;
}

/** The DJI flights matched to one aircraft (a logged drone flight is one aircraft's work on a record). */
export function djiFlightsForDrone(
  flights: DjiFlight[],
  droneId: string | null | undefined,
  droneSource: string | null | undefined
): DjiFlight[] {
  if (!droneId) return [];
  return flights.filter((f) => f.droneId === droneId && f.droneSource === droneSource);
}

/** DJI flights linked to a record whose aircraft has no logged drone flight on it. */
export function unmatchedDjiFlights(
  flights: DjiFlight[],
  loggedFlights: Array<{ droneId: string; droneSource: string }>
): DjiFlight[] {
  return flights.filter(
    (f) => !loggedFlights.some((logged) => logged.droneId === f.droneId && logged.droneSource === f.droneSource)
  );
}
